import * as React from "react";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { Objects } from "../../../../../../../react-core/Utils/Objects";
import { ExternalDocumentType } from "../../../../../../../Wrap/Models/Domains/Documents/ExternalDocumentType";
import { INoValue } from "../../../../../../../Wrap/Models/INoValue";
import { Instrument } from "../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Investment/Instrument";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Table } from "../../../../../../Shared/Table/Table";
import { ClientAccountStore } from "../../../../../Stroes/Variable";
import { AdviserContextStoreStatic } from "../../../NewBusinessWizard/Stores/Variable";
import { InstructionDocumentTypeRequestTriggerActionsCreator } from "../Actions/InstructionDocumentTypeRequestTriggerActionsCreator";
import { ExternalDocumentTypeMapper } from "../Resource/Content";

export interface IExternalFundDocModel extends INoValue {
    documentName: string;
    InvestmentDisplayName: string;
    fundUrl: string;
}

interface IExternalFundDocumentProps extends IProcessComponentProps {
    instrumentCodes: Array<string>;
    renderDownloadButtonSection?: boolean;
}

interface IExternalFundDocumentState {
    externalFundDocuments: Array<IExternalFundDocModel>;
    isShowFundFact: boolean;
    isShowKiidDoc: boolean;
    isShowSummaryBox: boolean;
    isShowFscs: boolean;
    isShowBondsmithTermsAndConditions: boolean;
    isDocLoading: boolean;
}

export class ExternalFundDocument extends ProcessComponent<IExternalFundDocumentProps, IExternalFundDocumentState> {
    constructor(props: IExternalFundDocumentProps) {
        super(props);

        this.state = {
            externalFundDocuments: [],
            isShowFundFact: false,
            isShowKiidDoc: false,
            isShowSummaryBox: false,
            isShowFscs: false,
            isShowBondsmithTermsAndConditions: false,
            isDocLoading: false,
        };
    }

    componentDidMount() {
        this.getExternalFundDocument(ExternalDocumentType.FundFactSheets);
        this.getExternalFundDocument(ExternalDocumentType.KiidDocuments);
        this.getExternalFundDocument(ExternalDocumentType.SummaryBoxes);
        this.getExternalFundDocument(ExternalDocumentType.FscsSheets);
        this.getExternalFundDocument(ExternalDocumentType.BondsmithTermsAndConditions);
    }

    private getExternalFundDocument(externalDocType: ExternalDocumentType) {
        this.setState({ isDocLoading: true }); // Indicate loading

        const { instrumentCodes } = this.props;
        const company = ClientAccountStore.getClientAccountInfo()?.Company || AdviserContextStoreStatic.getData()?.Company;

        InstructionDocumentTypeRequestTriggerActionsCreator.getExternalFundDocument(
            0,
            "",
            company,
            externalDocType,
            instrumentCodes,
            (callback) => {
                const existingModel = Objects.cloneObject(this.state.externalFundDocuments);
                const newDoc: Array<IExternalFundDocModel> = [];

                if (callback.Instruments.length > 0) {
                    callback.Instruments.forEach((item: Instrument) => {
                        if (item.FinexDocumentUrl !== '') {
                            newDoc.push({
                                documentName: ExternalDocumentTypeMapper[externalDocType],
                                InvestmentDisplayName: item.DisplayName,
                                fundUrl: item.FinexDocumentUrl,
                            });
                            switch (externalDocType) {
                                case ExternalDocumentType.FundFactSheets:
                                    this.setState({ isShowFundFact: true });
                                    break;
                                case ExternalDocumentType.KiidDocuments:
                                    this.setState({ isShowKiidDoc: true });
                                    break;
                                case ExternalDocumentType.SummaryBoxes:
                                    this.setState({ isShowSummaryBox: true });
                                    break;
                                case ExternalDocumentType.FscsSheets:
                                    this.setState({ isShowFscs: true });
                                    break;
                                case ExternalDocumentType.BondsmithTermsAndConditions:
                                    this.setState({ isShowBondsmithTermsAndConditions: true });
                                    break;
                            }
                        }
                    });

                    const updatedValue = existingModel.concat(newDoc);
                    this.setState({
                        externalFundDocuments: updatedValue,
                        isDocLoading: false,
                    });
                } else {
                    // No documents found, ensure the document type remains hidden
                    this.setState({ isDocLoading: false });
                }
            }
        );
    }

    private renderExternalDocTable() {
        const {
            isDocLoading,
            externalFundDocuments,
            isShowFundFact,
            isShowKiidDoc,
            isShowSummaryBox,
            isShowFscs,
            isShowBondsmithTermsAndConditions } = this.state;
        const documentName: string = !isShowFundFact
            ? ExternalDocumentTypeMapper[ExternalDocumentType.FundFactSheets]
            : !isShowKiidDoc
            ? ExternalDocumentTypeMapper[ExternalDocumentType.KiidDocuments]
            : !isShowSummaryBox
            ? ExternalDocumentTypeMapper[ExternalDocumentType.SummaryBoxes]
            : !isShowKiidDoc
            ? ExternalDocumentTypeMapper[ExternalDocumentType.FscsSheets]
            : !isShowBondsmithTermsAndConditions
            ? ExternalDocumentTypeMapper[ExternalDocumentType.BondsmithTermsAndConditions]
            : null;
        if (isDocLoading) {
            return <PartLoading isLoading={true}></PartLoading>;
        } else if (externalFundDocuments.length > 0) {
            return (
                <div className="row">
                    <div className="pt-24">
                        <div className="mb-50">
                            <Table
                                data={externalFundDocuments}
                                col={[
                                    {
                                        title: "Document",
                                        key: "LedgerDate",
                                        render: (data, row: IExternalFundDocModel, index) => {
                                            return (
                                                row.documentName +
                                                " - " +
                                                row.InvestmentDisplayName +
                                                " " +
                                                JsonDateParser.getFormatDateString(new Date(), "dd/MM/yyyy", true, true)
                                            );
                                        },
                                    },
                                    {
                                        title: "Link",
                                        key: "ProductDisplayName",
                                        render: (data, row: IExternalFundDocModel, index) => {
                                            return (
                                                <a href={encodeURI(row.fundUrl)} target="_blank">
                                                    Document
                                                </a>
                                            );
                                        },
                                    },
                                ]}
                            />
                        </div>
                    </div>
                </div>
            );
        }
        else if (!isShowFundFact && !isShowKiidDoc && !isShowSummaryBox &&
            !isShowFscs && !isShowBondsmithTermsAndConditions)
        {
            return (
                <Alert
                    alertType={AlertType.Warning}
                    message={<>{`There is no ${documentName} report based on selected investments.`}</>}
                />
            );
        }
    }

    private renderDownloadButtonSection(): JSX.Element
    {
        const {
            isShowFundFact,
            isShowKiidDoc,
            isShowSummaryBox,
            isShowFscs,
            isShowBondsmithTermsAndConditions } = this.state;

        return (
            <>
                {isShowFundFact && (
                    <div className="mb-24">
                        <span>Fund Fact Sheets</span>
                        <button
                            type="button"
                            className="download-button label-sr-only ml-20"
                            onClick={() => this.getExternalFundDocument(ExternalDocumentType.FundFactSheets)}
                        >
                            <span className="icon" aria-hidden="true"></span>
                            <span className="sr-only">Download</span>
                        </button>
                    </div>
                )}
                {isShowKiidDoc && (
                    <div className="mb-24">
                        <span>KIID/Simplified Prospectus</span>

                        <button
                            type="button"
                            className="download-button label-sr-only ml-20"
                            onClick={() => this.getExternalFundDocument(ExternalDocumentType.KiidDocuments)}
                        >
                            <span className="icon" aria-hidden="true"></span>
                            <span className="sr-only">Download</span>
                        </button>
                    </div>
                )}
                {isShowSummaryBox && (
                    <div className="mb-24">
                        <span>Cash Deposit Product Information Sheets</span>

                        <button
                            type="button"
                            className="download-button label-sr-only ml-20"
                            onClick={() => this.getExternalFundDocument(ExternalDocumentType.SummaryBoxes)}
                        >
                            <span className="icon" aria-hidden="true"></span>
                            <span className="sr-only">Download</span>
                        </button>
                    </div>
                )}
                {isShowFscs && (
                    <div className="mb-24">
                        <span>FSCS Information Sheets</span>

                        <button
                            type="button"
                            className="download-button label-sr-only ml-20"
                            onClick={() => this.getExternalFundDocument(ExternalDocumentType.FscsSheets)}
                        >
                            <span className="icon" aria-hidden="true"></span>
                            <span className="sr-only">Download</span>
                        </button>
                    </div>
                )}
                {isShowBondsmithTermsAndConditions && (
                    <div className="mb-24">
                        <span>Terms and Conditions</span>

                        <button
                            type="button"
                            className="download-button label-sr-only ml-20"
                            onClick={() =>
                                this.getExternalFundDocument(ExternalDocumentType.BondsmithTermsAndConditions)}
                        >
                            <span className="icon" aria-hidden="true"></span>
                            <span className="sr-only">Download</span>
                        </button>
                    </div>
                )}
            </>
        )
    }

    public render(): JSX.Element {
        return (
            <>
                { (this.props.renderDownloadButtonSection == null || this.props.renderDownloadButtonSection)
                    && this.renderDownloadButtonSection() }
                { this.renderExternalDocTable() }
            </>
        );
    }
}
